import { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import styled from '@/styles';
import { DependabilitySignal } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import ActivityFeed from './ActivityFeed';
import useDependabilityEvents from './useDependabilityEvents';
import { InfoTipIcon20, XMarkIcon } from './icons';

// GRAVY-10429: header banner explaining why the recency-weighted rows' % can
// differ from the raw count printed beneath them. Light-lavender surface, an
// info icon on the left, and "Weighted" bold in purple.
const WeightedBanner = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 12px',
  marginBottom: '24px',
  // Frame 1000006997: pill-shaped lavender banner (#E5E2FF) with a subtle border
  // (#B9B3EC) and ALL-purple text (#2A15E1), not just the bold "Weighted".
  borderRadius: '100px',
  border: `1px solid ${dependabilityV2Colors.depWeightedBorder}`,
  backgroundColor: dependabilityV2Colors.depWeightedBg,
  color: dependabilityV2Colors.depWeightedText,
  // Frame 1000006997 typography: Manrope 14px / 140%. Normal text is SemiBold
  // 600; the "Weighted" word is Bold 700 (WeightedBannerStrong below).
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: 1.4,
});
const WeightedBannerIcon = styled('span', {
  display: 'inline-flex',
  flex: 'none',
  color: dependabilityV2Colors.depWeightedText,
});
const WeightedBannerStrong = styled('strong', {
  fontWeight: 700,
  color: dependabilityV2Colors.depWeightedText,
});

// GRAVY-10422: the breakdown popover was redesigned (Figma "Frame 1000006997")
// from a single inline label|track|value line to a stacked per-metric block —
// a label (+ optional pill) with a right-aligned value, a full-width "how
// healthy" bar, and a count caption ("44 of 47 shifts arrived on time").
const SignalList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});
const SignalRow = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px', // frame: 8px internal gap between label line, bar, and caption
});
const SignalHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});
const SignalLabelGroup = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
});
const SignalLabel = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});
// Label pill: the late-drop magnitude (Low/Moderate/High) or the accept-rate
// "Below N% target" flag. XS 12/600, padding 2x8, radius 12; healthy = green
// tint, below-target = amber tint (both data-driven from the payload).
const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  border: '1px solid transparent',
  variants: {
    variant: {
      healthy: {
        backgroundColor: dependabilityV2Colors.depHealthyPillBg,
        borderColor: dependabilityV2Colors.depHealthyPillBorder,
        color: dependabilityV2Colors.depPositive,
      },
      below_target: {
        backgroundColor: dependabilityV2Colors.depWarnBg,
        borderColor: dependabilityV2Colors.depWarnPillBorder,
        color: dependabilityV2Colors.depWarnText,
      },
    },
  },
});
// GRAVY-10429: the purple "Weighted" pill shown on the recency-weighted rows in
// place of a status pill. Same dimensions as Pill, lavender fill + purple text.
const WeightedPill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  // Frame 1000006997: subtle purple outline, matching the Low / below-target pills.
  border: `1px solid ${dependabilityV2Colors.depWeightedBorder}`,
  backgroundColor: dependabilityV2Colors.depWeightedBg,
  color: dependabilityV2Colors.depWeightedText,
});
// GRAVY-10429: purple note appended to a weighted row's caption — " (raw: 67%)"
// for on-time/completion, or " (recency-weighted)" for client rating.
const CaptionNote = styled('span', {
  // Frame 1000006997: the raw/weighted note sits a touch apart from the count
  // caption, in the same #2A15E1 as the banner + pills, slightly emphasised.
  marginLeft: '6px',
  fontWeight: 500,
  color: dependabilityV2Colors.depWeightedText,
});
const MeterTrack = styled('div', {
  width: '100%',
  height: '12px',
  borderRadius: '100px',
  backgroundColor: dependabilityV2Colors.depMeterTrack,
  overflow: 'hidden',
});
const MeterFill = styled('div', {
  height: '12px',
  borderRadius: '100px',
});
const SignalValue = styled('div', {
  fontSize: '20px',
  lineHeight: 1.4,
  fontWeight: 800,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  flex: 'none',
  variants: {
    status: {
      ok: { color: dependabilityV2Colors.depPositive },
      warn: { color: dependabilityV2Colors.depWarnText },
    },
  },
});
const Caption = styled('div', {
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});
const Footer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  // 40px section gap above the divider per the frame's container gap
  marginTop: '40px',
  paddingTop: '16px',
  borderTop: `1px solid ${dependabilityV2Colors.depBorder}`,
  fontSize: '$md',
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});

interface Props {
  workerId: number;
  isOpen: boolean;
  onClose: () => void;
  signals?: DependabilitySignal[];
  completedShifts?: number;
  scoringMode?: 'activation' | 'reliability';
}

const PER_PAGE = 20;

const fillColor = (status: DependabilitySignal['status']) =>
  status === 'warn'
    ? dependabilityV2Colors.depFeedNeutral
    : dependabilityV2Colors.depFeedPositive;

const ScoreHistoryModal = ({
  workerId,
  isOpen,
  onClose,
  signals = [],
  completedShifts = 0,
  scoringMode = 'reliability',
}: Props) => {
  const hasSignals = signals.length > 0;
  const { events, page, loading, loaded, error, loadPage, retry, hasMore } =
    useDependabilityEvents(workerId, PER_PAGE);

  useEffect(() => {
    // event history is only the fallback body for payloads without signals
    if (isOpen && !hasSignals) loadPage(1);
  }, [isOpen, hasSignals, loadPage]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      // GRAVY-10422 frame (1000006997): 650w (602 rows + 24 padding each side),
      // radius 8, flat shadow, 70% scrim, 24px padding, 40px gap between the
      // title / body / footer sections.
      PaperProps={{
        sx: {
          // Frame 1000006997: 652px content row (label|value space-between) + 24px
          // padding each side = 700px paper. The weighted banner text fits on one
          // line at this width.
          width: 700,
          maxWidth: '95vw',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      }}
    >
      <DialogTitle
        sx={{
          padding: '24px 24px 0',
          fontSize: 18,
          fontWeight: 600,
          color: dependabilityV2Colors.depInk,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Dependability score breakdown
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: dependabilityV2Colors.depInk }}
        >
          <XMarkIcon />
        </IconButton>
      </DialogTitle>
      {/* !important: MUI forces paddingTop 0 on a DialogContent that follows
          a DialogTitle via a two-class sibling selector that outranks sx */}
      <DialogContent
        sx={{ padding: '40px 24px 24px', paddingTop: '40px !important' }}
      >
        {hasSignals ? (
          <>
            {/* GRAVY-10429: the weighted-rows explainer, only when the payload
                carries at least one recency-weighted row (reliability mode). */}
            {signals.some((signal) => signal.weighted) && (
              <WeightedBanner data-testid="weighted-banner">
                <WeightedBannerIcon>
                  <InfoTipIcon20 />
                </WeightedBannerIcon>
                <span>
                  <WeightedBannerStrong>Weighted</WeightedBannerStrong> rows
                  favor recent shifts, so the % may differ slightly from the raw
                  count below it.
                </span>
              </WeightedBanner>
            )}
            <SignalList data-testid="signal-breakdown">
              {signals.map((signal) => (
                <SignalRow
                  key={signal.key}
                  data-testid={`signal-${signal.key}`}
                >
                  <SignalHeader>
                    <SignalLabelGroup>
                      <SignalLabel>{signal.label}</SignalLabel>
                      {/* A weighted row shows only the "Weighted" pill; a plain
                          row keeps its status pill (Low / Below N% target). */}
                      {signal.weighted ? (
                        <WeightedPill data-testid="weighted-pill">
                          Weighted
                        </WeightedPill>
                      ) : (
                        signal.pill && (
                          <Pill
                            variant={signal.pill.variant}
                            data-testid={`signal-${signal.key}-pill`}
                          >
                            {signal.pill.label}
                          </Pill>
                        )
                      )}
                    </SignalLabelGroup>
                    <SignalValue status={signal.status}>
                      {signal.display_value}
                    </SignalValue>
                  </SignalHeader>
                  {/* The bar is the "how healthy" meter; activation deltas have
                      no percent, so their rows show only the label + value. */}
                  {signal.percent != null && (
                    <MeterTrack>
                      <MeterFill
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100, signal.percent),
                          )}%`,
                          backgroundColor: fillColor(signal.status),
                        }}
                      />
                    </MeterTrack>
                  )}
                  {signal.caption && (
                    <Caption>
                      {signal.caption}
                      {/* GRAVY-10429: purple weighting note after the base
                          caption — raw % for on-time/completion, otherwise
                          the recency-weighted marker for client rating. */}
                      {signal.weighted && (
                        <CaptionNote data-testid={`signal-${signal.key}-note`}>
                          {signal.raw_percent != null
                            ? ` (raw: ${signal.raw_percent}%)`
                            : ' (recency-weighted)'}
                        </CaptionNote>
                      )}
                    </Caption>
                  )}
                </SignalRow>
              ))}
            </SignalList>
            <Footer>
              <InfoTipIcon20 />
              {scoringMode === 'activation'
                ? 'Activation score, based on onboarding progress'
                : `Based on ${completedShifts} shifts`}
            </Footer>
          </>
        ) : (
          <ActivityFeed
            events={events}
            hasMore={hasMore}
            loading={loading || !loaded}
            // A failed events fetch must surface as a retryable note, not the
            // false "No score activity yet" (matches RecentActivityModal).
            // Retry targets the page that was actually last ATTEMPTED (fix round
            // 5), so a failed reopen page-1 refresh is retried as page 1, not
            // appended as page N+1 from a stale prior-session page.
            error={error}
            onRetry={retry}
            onViewMore={() => loadPage(page + 1)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScoreHistoryModal;
